import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ProductsView.css';

// ─────────────────────────────────────────────────────────────────────────────
// API CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';

function getAuthToken() {
  return (
    localStorage.getItem('authToken')    ||
    localStorage.getItem('token')        ||
    localStorage.getItem('adminToken')   ||
    localStorage.getItem('admin_token')  ||
    localStorage.getItem('auth_token')   ||
    sessionStorage.getItem('authToken')  ||
    sessionStorage.getItem('token')      ||
    sessionStorage.getItem('adminToken') ||
    ''
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PLAN CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const PLAN_VALIDITY_DAYS = {
  free: 14, basic: 30, pro: 60, business: 90,
  standard: 60, premium: 90, gold: 90, platinum: 180, diamond: 365,
  featured: 30, boosted: 14,
};

function getPlanValidityDays(planName) {
  if (!planName) return 14;
  const key = planName.toLowerCase().replace(/[^a-z]/g, '');
  return PLAN_VALIDITY_DAYS[key] ?? 14;
}

function getDaysRemaining(product) {
  if (product.validity_days !== undefined && product.validity_days !== null) {
    return Math.max(0, Number(product.validity_days));
  }
  const expField = product.expires_at || product.expiry_date || product.expiration_date;
  if (expField) {
    return Math.max(0, Math.ceil((new Date(expField) - new Date()) / 86400000));
  }
  const dateAdded = product.date_added || product.created_at;
  if (!dateAdded) return null;
  const addedDate = new Date(dateAdded);
  if (isNaN(addedDate)) return null;
  const totalDays  = getPlanValidityDays(product.premium_plan || 'free');
  const expiryDate = new Date(addedDate.getTime() + totalDays * 86400000);
  return Math.max(0, Math.ceil((expiryDate - new Date()) / 86400000));
}

// ─────────────────────────────────────────────────────────────────────────────
// VIDEO FRAME CAPTURE
// ─────────────────────────────────────────────────────────────────────────────
function useVideoFrame(videoUrl, fallbackImageUrl) {
  const [frameUrl, setFrameUrl] = useState(null);
  const [status,   setStatus]   = useState('idle');

  useEffect(() => {
    setFrameUrl(null);
    setStatus('idle');
    if (!videoUrl) return;
    setStatus('loading');
    let cancelled = false;

    const vid = document.createElement('video');
    vid.crossOrigin  = 'anonymous';
    vid.preload      = 'metadata';
    vid.muted        = true;
    vid.playsInline  = true;
    vid.src          = videoUrl;

    const capture = () => {
      if (cancelled) return;
      try {
        const canvas  = document.createElement('canvas');
        canvas.width  = 120;
        canvas.height = 120;
        const ctx     = canvas.getContext('2d');
        ctx.drawImage(vid, 0, 0, 120, 120);
        const data    = canvas.toDataURL('image/jpeg', 0.85);
        if (data && data.length > 1200) {
          setFrameUrl(data);
          setStatus('done');
        } else {
          vid.currentTime = Math.min(vid.duration / 2, 3);
        }
      } catch {
        setStatus('error');
      }
    };

    vid.addEventListener('loadedmetadata', () => {
      if (cancelled) return;
      vid.currentTime = Math.min(1, vid.duration * 0.1);
    });
    vid.addEventListener('seeked', capture);
    vid.addEventListener('error',  () => { if (!cancelled) setStatus('error'); });

    const timer = setTimeout(() => {
      if (!cancelled && status !== 'done') setStatus('error');
    }, 8000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      vid.src = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  const thumbnail = frameUrl || fallbackImageUrl || null;
  return { thumbnail, captureStatus: status };
}

// ─────────────────────────────────────────────────────────────────────────────
// VIDEO THUMB COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function VideoThumb({ product }) {
  const videoUrl    = product.primary_video  || product.video_url || product.video || null;
  const posterUrl   = product.video_thumbnail || null;
  const fallbackImg = !videoUrl ? (product.primary_image || product.image || null) : null;
  const { thumbnail } = useVideoFrame(posterUrl ? null : videoUrl, posterUrl || fallbackImg);
  const BLANK_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'%3E%3Crect width='48' height='48' rx='8' fill='%230f172a'/%3E%3C/svg%3E";

  return (
    <div style={{
      position: 'relative', width: 48, height: 48,
      borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: '#0f172a',
    }}>
      <img
        src={thumbnail || BLANK_SVG}
        alt="video"
        onError={e => { e.target.src = BLANK_SVG; }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.28)' }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 20, height: 20, borderRadius: '50%',
          background: 'rgba(255,255,255,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
        }}>
          <svg width="8" height="8" viewBox="0 0 24 24" fill="#111"><path d="M8 5v14l11-7z" /></svg>
        </div>
      </div>
      <div style={{
        position: 'absolute', bottom: 3, left: 3,
        background: 'rgba(0,0,0,0.72)', color: '#fff',
        fontSize: '7px', fontWeight: 700, padding: '1px 4px',
        borderRadius: 3, letterSpacing: '0.4px',
      }}>VIDEO</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT THUMBNAIL
// ─────────────────────────────────────────────────────────────────────────────
const VIDEO_EXTS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
const looksLikeVideo = (url) =>
  Boolean(url) && VIDEO_EXTS.some(ext => String(url).toLowerCase().includes(ext));

function ProductThumb({ product }) {
  const hasVideo = Boolean(
    product.primary_video || product.video_url || product.video ||
    (product.videos && product.videos[0]) ||
    looksLikeVideo(product.primary_image)
  );
  if (hasVideo) return <VideoThumb product={product} />;

  const imgSrc =
    product.primary_image || product.image ||
    (product.images && product.images[0]?.image_url) || null;

  if (imgSrc) {
    return (
      <img src={imgSrc} alt={product.title} className="product-thumb"
        onError={e => { e.target.style.display = 'none'; }}
        style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: 48, height: 48, borderRadius: 8, flexShrink: 0,
      background: 'linear-gradient(135deg,#e2e8f0,#cbd5e1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="#94a3b8" strokeWidth="2"/>
        <circle cx="8.5" cy="8.5" r="1.5" fill="#94a3b8"/>
        <polyline points="21 15 16 10 5 21" stroke="#94a3b8" strokeWidth="2"/>
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AD VALIDITY BADGE
// ─────────────────────────────────────────────────────────────────────────────
function AdValidityBadge({ days }) {
  if (days === null || days === undefined)
    return <span style={{ color: '#94a3b8', fontSize: 13 }}>—</span>;
  if (days === 0)
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
        background: '#fef2f2', color: '#dc2626', border: '1px solid #dc262633',
      }}>⛔ Expired</span>
    );
  const urgent  = days <= 3;
  const warning = days <= 7;
  const bg    = urgent ? '#fef2f2'  : warning ? '#fffbeb'  : '#f0fdf4';
  const color = urgent ? '#dc2626'  : warning ? '#d97706'  : '#16a34a';
  const icon  = urgent ? '🔴' : warning ? '🟡' : '🟢';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: bg, color, border: `1px solid ${color}33`, whiteSpace: 'nowrap',
    }}>{icon} {days}d left</span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ADJUST VALIDITY CELL
// ─────────────────────────────────────────────────────────────────────────────
function AdjustValidityCell({ product, currentDays, onAdjusted }) {
  const days      = currentDays !== undefined ? currentDays : getDaysRemaining(product);
  const isExpired = days === 0;
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const persist = useCallback(async (targetDays) => {
    setSaving(true);
    setError('');
    try {
      const token = getAuthToken();
      const res = await fetch(
        `${API_BASE_URL}/admin/products/${product.id}/adjust-validity/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Token ${token}` } : {}),
          },
          body: JSON.stringify({ target_days: targetDays }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Server error');
      const confirmed = data.validity_days ?? targetDays;
      if (onAdjusted) onAdjusted(product.id, confirmed, product.title);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [product.id, product.title, onAdjusted]);

  const adjust = useCallback((delta) => {
    const base    = (isExpired && delta > 0) ? 0 : days;
    const newDays = Math.max(0, base + delta);
    if (onAdjusted) onAdjusted(product.id, newDays, product.title);
    persist(newDays);
  }, [days, isExpired, product.id, product.title, onAdjusted, persist]);

  const Btn = ({ delta, reduceStyle }) => {
    const disabled = saving || (delta < 0 && days === 0);
    return (
      <button
        onClick={() => adjust(delta)}
        disabled={disabled}
        title={delta < 0 ? 'Reduce by 1 day' : 'Extend by 1 day'}
        style={{
          width: 30, height: 30, borderRadius: 6,
          border: `1.5px solid ${reduceStyle ? '#fca5a5' : '#86efac'}`,
          background: disabled ? '#f3f4f6' : reduceStyle ? '#fef2f2' : '#f0fdf4',
          color:      disabled ? '#9ca3af' : reduceStyle ? '#dc2626' : '#16a34a',
          fontSize: 16, fontWeight: 700,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1, transition: 'all .15s',
        }}
      >{delta < 0 ? '\u2212' : '+'}</button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 90 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Btn delta={-1} reduceStyle />
        <div style={{
          minWidth: 44, textAlign: 'center',
          padding: '5px 8px', borderRadius: 8,
          background: saving ? '#f8fafc' : isExpired ? '#fef2f2' : '#f0fdf4',
          border: `1.5px solid ${saving ? '#d1d5db' : isExpired ? '#fca5a5' : '#86efac'}`,
          fontSize: 13, fontWeight: 800,
          color: saving ? '#9ca3af' : isExpired ? '#dc2626' : '#16a34a',
        }}>
          {saving ? '\u2026' : `${days ?? '?'}d`}
        </div>
        <Btn delta={+1} reduceStyle={false} />
      </div>
      {saving && (
        <span style={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>saving\u2026</span>
      )}
      {error && !saving && (
        <span style={{ fontSize: 9, color: '#dc2626', fontWeight: 600, maxWidth: 90,
                       textAlign: 'center', lineHeight: 1.2 }}>{error}</span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PREMIUM BADGE
// ─────────────────────────────────────────────────────────────────────────────
function PremiumBadge({ product }) {
  const raw  = product.premium_plan || 'free';
  const plan = raw.toLowerCase().replace(/[^a-z]/g, '');
  const cfg  = {
    free:     { label: '👑 Free',     bg: '#f8fafc', color: '#64748b', border: '#cbd5e1' },
    basic:    { label: '⚡ Basic',    bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' },
    pro:      { label: '💎 Pro',      bg: '#fdf4ff', color: '#a21caf', border: '#e879f9' },
    business: { label: '⭐ Business', bg: '#fffbeb', color: '#d97706', border: '#fcd34d' },
    standard: { label: '🔷 Standard', bg: '#f5f3ff', color: '#7c3aed', border: '#c4b5fd' },
    premium:  { label: '💎 Premium',  bg: '#fdf4ff', color: '#a21caf', border: '#e879f9' },
    gold:     { label: '🥇 Gold',     bg: '#fffbeb', color: '#d97706', border: '#fcd34d' },
    platinum: { label: '🪙 Platinum', bg: '#f0fdfa', color: '#0d9488', border: '#5eead4' },
    diamond:  { label: '💠 Diamond',  bg: '#ecfeff', color: '#0891b2', border: '#67e8f9' },
    featured: { label: '⭐ Featured', bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
    boosted:  { label: '🚀 Boosted',  bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  };
  const c = cfg[plan] || cfg.free;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`, whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPIRY MODE TOGGLE
// ─────────────────────────────────────────────────────────────────────────────
function ExpiryModeToggle({ mode, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: '#fff', border: '1px solid #e5e7eb',
      borderRadius: 10, padding: '6px 14px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
        stroke={mode === 'auto' ? '#dc2626' : '#6b7280'} strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Expired ads:</span>
      <button
        onClick={() => onChange(mode === 'auto' ? 'manual' : 'auto')}
        style={{
          display: 'flex', alignItems: 'center', gap: 0,
          background: mode === 'auto' ? '#fef2f2' : '#f0fdf4',
          border: `1.5px solid ${mode === 'auto' ? '#fca5a5' : '#86efac'}`,
          borderRadius: 99, padding: '3px 4px 3px 10px',
          cursor: 'pointer', fontSize: 12, fontWeight: 700,
          color: mode === 'auto' ? '#dc2626' : '#16a34a',
        }}
      >
        {mode === 'auto' ? '🗑️ Auto-delete' : '🖐 Manual'}
        <span style={{
          marginLeft: 8, width: 22, height: 22, borderRadius: '50%',
          background: mode === 'auto' ? '#dc2626' : '#16a34a',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {mode === 'auto'
            ? <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2.5"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="white" strokeWidth="2.5"/></svg>
            : <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="white" strokeWidth="2.5"/></svg>
          }
        </span>
      </button>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>
        {mode === 'auto' ? 'Expired ads removed automatically' : 'You delete expired ads manually'}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GHOST MODE TOGGLE  (mirrors ExpiryModeToggle style exactly)
// ─────────────────────────────────────────────────────────────────────────────
function GhostModeToggle({ mode, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: '#fff', border: '1px solid #e5e7eb',
      borderRadius: 10, padding: '6px 14px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <span style={{ fontSize: 15, lineHeight: 1 }}>👻</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Ghost ads:</span>
      <button
        onClick={() => onChange(mode === 'auto' ? 'manual' : 'auto')}
        style={{
          display: 'flex', alignItems: 'center', gap: 0,
          background: mode === 'auto' ? '#fef2f2' : '#f0fdf4',
          border: `1.5px solid ${mode === 'auto' ? '#fca5a5' : '#86efac'}`,
          borderRadius: 99, padding: '3px 4px 3px 10px',
          cursor: 'pointer', fontSize: 12, fontWeight: 700,
          color: mode === 'auto' ? '#dc2626' : '#16a34a',
        }}
      >
        {mode === 'auto' ? '🗑️ Auto-clean' : '🖐 Manual'}
        <span style={{
          marginLeft: 8, width: 22, height: 22, borderRadius: '50%',
          background: mode === 'auto' ? '#dc2626' : '#16a34a',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {mode === 'auto'
            ? <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2.5"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="white" strokeWidth="2.5"/></svg>
            : <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="white" strokeWidth="2.5"/></svg>
          }
        </span>
      </button>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>
        {mode === 'auto' ? 'Ghost ads cleaned on load' : 'Click button to clean manually'}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE CONFIRM MODAL
// ─────────────────────────────────────────────────────────────────────────────
function DeleteConfirmModal({ product, onConfirm, onCancel }) {
  if (!product) return null;
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
               alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400,
                 boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
      >
        <div style={{ padding: '28px 28px 0', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: '#fef2f2',
            margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: '#111' }}>Delete Product?</h3>
          <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>This will permanently remove</p>
          <p style={{
            margin: '0 0 20px', fontSize: 13, fontWeight: 600, color: '#111',
            background: '#f9fafb', padding: '8px 14px', borderRadius: 8,
            border: '1px solid #e5e7eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>"{product.title}"</p>
          <p style={{ margin: '0 0 24px', fontSize: 12, color: '#9ca3af' }}>This action cannot be undone.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '0 28px 24px' }}>
          <button onClick={onCancel}
            style={{ flex: 1, padding: '11px 0', borderRadius: 9, cursor: 'pointer',
                     border: '1.5px solid #d1d5db', background: '#fff', fontSize: 14, fontWeight: 600, color: '#374151' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
          >Cancel</button>
          <button onClick={onConfirm}
            style={{ flex: 1, padding: '11px 0', borderRadius: 9, cursor: 'pointer',
                     border: 'none', background: '#dc2626', fontSize: 14, fontWeight: 600, color: '#fff' }}
            onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'}
            onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}
          >Delete</button>
        </div>
      </div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return 'N/A';
  const p = new Date(d);
  if (isNaN(p)) return String(d);
  return p.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
// ✅ productStats prop added — provides server-authoritative counts from AdminDashboard
// so Total Products shows the real DB total (e.g. 847) not just the loaded page count.
function ProductsView({ products, productStats, handleDeleteProduct }) {
  const [searchTerm,       setSearchTerm]      = useState('');
  const [statusFilter,     setStatusFilter]     = useState('all');
  const [categoryFilter,   setCategoryFilter]   = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentPage,      setCurrentPage]      = useState(1);
  const [productsPerPage,  setProductsPerPage]  = useState(10);
  const [pageSizeInput,    setPageSizeInput]    = useState('10');

  const [localProducts, setLocalProducts] = useState(products || []);
  const [deleteModal,   setDeleteModal]   = useState(null);
  const [validityCache, setValidityCache] = useState({});
  const [toastMsg,      setToastMsg]      = useState('');
  const [expiryMode,    setExpiryMode]    = useState(() => {
    try { return localStorage.getItem('adminExpiryMode') || 'manual'; } catch { return 'manual'; }
  });
  const [ghostMode, setGhostMode] = useState(() => {
    try { return localStorage.getItem('adminGhostMode') || 'auto'; } catch { return 'auto'; }
  });

  // Ref so autorun ghost cleanup only fires once per products load, not on every re-render
  const ghostAutoRanRef = React.useRef(false);

  useEffect(() => {
    const incoming = products || [];
    setLocalProducts(incoming);
    setValidityCache(prev => {
      const next = { ...prev };
      incoming.forEach(p => {
        if (p.validity_days !== undefined && p.validity_days !== null) {
          next[p.id] = Number(p.validity_days);
        }
      });
      return next;
    });
    // Auto-run ghost cleanup when products load if mode is 'auto'
    if (incoming.length > 0 && !ghostAutoRanRef.current) {
      ghostAutoRanRef.current = true;
      // Read mode fresh from localStorage so we don't depend on stale closure state
      const currentGhostMode = (() => {
        try { return localStorage.getItem('adminGhostMode') || 'auto'; } catch { return 'auto'; }
      })();
      if (currentGhostMode === 'auto') {
        // Small delay so UI renders first
        setTimeout(() => cleanupGhostAds(), 1500);
      }
    }
  }, [products]);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };

  const handleValidityAdjusted = useCallback((productId, newDays, title) => {
    setValidityCache(prev => ({ ...prev, [productId]: newDays }));
    showToast(`✓ Saved: ${newDays}d for "${title}"`);
  }, []);

  const handleExpiryModeChange = (newMode) => {
    setExpiryMode(newMode);
    try { localStorage.setItem('adminExpiryMode', newMode); } catch {}
    if (newMode === 'auto') {
      runAutoDelete(localProducts, validityCache);
      showToast('Auto-delete enabled — expired ads removed');
    } else {
      showToast('Manual mode — you control when expired ads are deleted');
    }
  };

  const handleGhostModeChange = (newMode) => {
    setGhostMode(newMode);
    try { localStorage.setItem('adminGhostMode', newMode); } catch {}
    if (newMode === 'auto') {
      cleanupGhostAds();
      showToast('Ghost auto-clean enabled — scanning now...');
    } else {
      showToast('Ghost manual mode — use the button to clean when needed');
    }
  };

  const runAutoDelete = (currentProducts, currentValidityCache) => {
    const expiredIds = currentProducts.filter(p => {
      const d = currentValidityCache[p.id] !== undefined
        ? currentValidityCache[p.id]
        : getDaysRemaining(p);
      return d === 0;
    }).map(p => p.id);
    if (!expiredIds.length) return;
    expiredIds.forEach(id => handleDeleteProduct(id));
    setLocalProducts(prev => prev.filter(p => !expiredIds.includes(p.id)));
    showToast(`Auto-deleted ${expiredIds.length} expired ad${expiredIds.length > 1 ? 's' : ''}`);
  };

  // ── cleanupGhostAds ─────────────────────────────────────────────────────────
  // Fetches ALL transactions from the ledger, builds a Set of ad IDs that have
  // at least one completed payment, then deletes every non-free ad NOT in that Set.
  // Uses the same admin token already in scope — no extra auth needed.
  const [ghostCleanupState, setGhostCleanupState] = useState('idle'); // idle | running | done
  const [ghostCount, setGhostCount] = useState(null); // null = not scanned yet

  const cleanupGhostAds = async () => {
    const token = getAuthToken();
    if (!token) { showToast('❌ No auth token found'); return; }

    setGhostCleanupState('running');
    showToast('🔍 Scanning for ghost ads...');

    try {
      // ── Step 1: Fetch full transaction ledger (all pages) ─────────────────
      let allTxns = [];
      let url = `${API_BASE_URL}/auth/admin/transactions/?page_size=200`;
      while (url) {
        const res = await fetch(url, { headers: { Authorization: `Token ${token}` } });
        if (!res.ok) { showToast(`❌ Ledger fetch failed (${res.status})`); setGhostCleanupState('idle'); return; }
        const data = await res.json();
        const page = Array.isArray(data) ? data : (data.results || []);
        allTxns = allTxns.concat(page);
        url = (!Array.isArray(data) && data.next) ? data.next : null;
      }

      // ── Step 2: Build Set of ad IDs with a completed payment ─────────────
      const completedAdIds = new Set(
        allTxns
          .filter(t => ['completed','success','paid'].includes((t.status||'').toLowerCase()))
          .map(t => t.ad_id ?? t.ad ?? null)
          .filter(id => id !== null)
      );

      // ── Step 3: Find ghost ads in the currently loaded product list ───────
      const ghosts = localProducts.filter(ad => {
        const plan = (ad.premium_plan || ad.plan || '').toLowerCase().replace(/[^a-z]/g,'');
        if (!plan || plan === 'free') return false;
        return !completedAdIds.has(ad.id);
      });

      // Update the count badge immediately so admin sees it before deletion
      setGhostCount(ghosts.length);

      if (!ghosts.length) {
        showToast('✅ No ghost ads found — all clean!');
        setGhostCleanupState('done');
        setTimeout(() => setGhostCleanupState('idle'), 3000);
        return;
      }

      // ── Step 4: Delete each ghost ad ──────────────────────────────────────
      let deleted = 0;
      await Promise.allSettled(
        ghosts.map(async (ad) => {
          try {
            const r = await fetch(`${API_BASE_URL}/ads/${ad.id}/`, {
              method: 'DELETE',
              headers: { Authorization: `Token ${token}` },
            });
            if (r.ok || r.status === 204 || r.status === 404) {
              handleDeleteProduct(ad.id);
              setLocalProducts(prev => prev.filter(p => p.id !== ad.id));
              deleted++;
            }
          } catch {}
        })
      );

      setGhostCount(0);
      showToast(`🗑️ Cleaned ${deleted} ghost ad${deleted !== 1 ? 's' : ''} (${allTxns.length} txns checked)`);
      setGhostCleanupState('done');
      setTimeout(() => setGhostCleanupState('idle'), 4000);
    } catch (err) {
      console.error('[cleanupGhostAds]', err);
      showToast('❌ Cleanup failed — see console');
      setGhostCleanupState('idle');
    }
  };

  const handlePageSizeChange = () => {
    const size = parseInt(pageSizeInput, 10);
    if (!isNaN(size) && size >= 1 && size <= 200) {
      setProductsPerPage(size);
      setCurrentPage(1);
    } else {
      alert('Please enter a number between 1 and 200');
      setPageSizeInput(String(productsPerPage));
    }
  };

  const confirmDelete = () => {
    if (!deleteModal) return;
    handleDeleteProduct(deleteModal.id);
    setLocalProducts(prev => prev.filter(p => p.id !== deleteModal.id));
    showToast(`"${deleteModal.title}" deleted`);
    setDeleteModal(null);
  };

  useEffect(() => {
    if (!localProducts.length) return;
    const p = localProducts[0];
    console.group('📦 ProductsView field check');
    console.log('primary_video:', p.primary_video, '| video_thumbnail:', p.video_thumbnail);
    console.log('validity_days:', p.validity_days, '| premium_plan:', p.premium_plan);
    console.log('image_count:', p.image_count, '| video_count:', p.video_count, '| like_count:', p.like_count);
    console.groupEnd();
  }, [localProducts]);

  const getStatus   = p => p.status || (p.is_active ? 'Active' : 'Inactive');
  const getCategory = p => p.category_name || p.category || 'N/A';

  // ✅ Use server-authoritative counts when available (productStats from AdminDashboard).
  // total uses data.count from DRF — always the real DB total across all pages.
  // active/inactive/newLast7 fall back to local computation when not provided.
  // expired is always computed locally from validityCache (no server equivalent needed).
  const stats = {
    total:    productStats?.total    ?? localProducts.length,
    active:   productStats?.active   ?? localProducts.filter(p => getStatus(p) === 'Active').length,
    inactive: productStats?.inactive ?? localProducts.filter(p => getStatus(p) === 'Inactive').length,
    newLast7: productStats?.newLast7 ?? localProducts.filter(p => {
      const d = new Date(p.date_added || p.created_at);
      const ago = new Date(); ago.setDate(ago.getDate() - 7);
      return !isNaN(d) && d >= ago;
    }).length,
    expired:  localProducts.filter(p => {
      const d = validityCache[p.id] !== undefined ? validityCache[p.id] : getDaysRemaining(p);
      return d === 0;
    }).length,
  };

  const categories = ['all', ...new Set(
    localProducts.map(getCategory).filter(c => c && c !== 'N/A')
  )];

  const filtered = localProducts.filter(p => {
    const email = p.posted_by_email || p.user || '';
    const cat   = getCategory(p);
    const q     = searchTerm.toLowerCase();
    const matchSearch   = !q || p.title?.toLowerCase().includes(q) || email.toLowerCase().includes(q) || cat.toLowerCase().includes(q);
    const matchStatus   = statusFilter   === 'all' || getStatus(p).toLowerCase() === statusFilter;
    const matchCategory = categoryFilter === 'all' || cat === categoryFilter;
    return matchSearch && matchStatus && matchCategory;
  });

  const lastIdx    = currentPage * productsPerPage;
  const firstIdx   = lastIdx - productsPerPage;
  const paginated  = filtered.slice(firstIdx, lastIdx);
  const totalPages = Math.ceil(filtered.length / productsPerPage);

  const handleSelectAll = e =>
    setSelectedProducts(e.target.checked ? paginated.map(p => p.id) : []);
  const toggleSelect = id =>
    setSelectedProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="products-view">

      {/* ── Toast ── */}
      {toastMsg && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9998,
          background: '#1f2937', color: '#fff', borderRadius: 8,
          padding: '12px 20px', fontSize: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          display: 'flex', alignItems: 'center', gap: 8, animation: 'fadeIn .2s',
        }}>
          {toastMsg}
        </div>
      )}

      {/* ── Header ── */}
      <div className="page-header-products">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">Manage all listed products from your marketplace</p>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="stats-grid-products">
        {[
          { label: 'Total Products', value: stats.total,    color: 'blue',
            icon: <><path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Z" stroke="currentColor" strokeWidth="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" stroke="currentColor" strokeWidth="2"/></> },
          { label: 'Active',         value: stats.active,   color: 'green',
            icon: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2"/><polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2"/></> },
          { label: 'Inactive',       value: stats.inactive, color: 'red',
            icon: <><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/><line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/></> },
          { label: 'New (7d)',        value: stats.newLast7, color: 'purple',
            icon: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2"/> },
        ].map(({ label, value, color, icon }) => (
          <div key={label} className="stat-card-product">
            <div className={`stat-icon-product ${color}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">{icon}</svg>
            </div>
            <div className="stat-content-product">
              <div className="stat-value-product">{String(value)}</div>
              <div className="stat-label-product">{label}</div>
            </div>
          </div>
        ))}

        {/* Expired card */}
        <div className="stat-card-product" style={{ borderLeft: '3px solid #dc2626' }}>
          <div className="stat-icon-product" style={{ background: '#fef2f2', color: '#dc2626' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="stat-content-product">
            <div className="stat-value-product" style={{ color: stats.expired > 0 ? '#dc2626' : undefined }}>
              {stats.expired}
            </div>
            <div className="stat-label-product">Expired</div>
          </div>
        </div>

        {/* Ghost ads card */}
        <div className="stat-card-product" style={{ borderLeft: '3px solid #7c3aed' }}>
          <div className="stat-icon-product" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C8.13 2 5 5.13 5 9v7l-2 2v1h18v-1l-2-2V9c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
              <path d="M5 18c0 0 1.5-1 3.5-1s3.5 1 3.5 1 1.5-1 3.5-1 3.5 1 3.5 1" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="stat-content-product">
            <div className="stat-value-product" style={{ color: ghostCount > 0 ? '#7c3aed' : undefined }}>
              {ghostCount === null ? '—' : ghostCount}
            </div>
            <div className="stat-label-product">
              Ghost Ads
              {ghostCount === null && (
                <span style={{ fontSize: 9, color: '#9ca3af', display: 'block', marginTop: 1 }}>scanning on load</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Expiry mode + Ghost Ad Cleanup ── */}
      <div style={{ margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <ExpiryModeToggle mode={expiryMode} onChange={handleExpiryModeChange} />
        <GhostModeToggle mode={ghostMode} onChange={handleGhostModeChange} />

        {/* Manual trigger button — always visible so admin can force a re-scan */}
        <button
          onClick={cleanupGhostAds}
          disabled={ghostCleanupState === 'running'}
          title="Manually trigger ghost ad scan now"
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '8px 16px', borderRadius: 8, border: '1.5px solid',
            borderColor: ghostCleanupState === 'done' ? '#16a34a' : ghostCleanupState === 'running' ? '#d97706' : '#dc2626',
            background: ghostCleanupState === 'done' ? '#f0fdf4' : ghostCleanupState === 'running' ? '#fffbeb' : '#fff5f5',
            color:  ghostCleanupState === 'done' ? '#16a34a' : ghostCleanupState === 'running' ? '#d97706' : '#dc2626',
            fontWeight: 700, fontSize: 13, cursor: ghostCleanupState === 'running' ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s', whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 15 }}>
            {ghostCleanupState === 'running' ? '⏳' : ghostCleanupState === 'done' ? '✅' : '👻'}
          </span>
          {ghostCleanupState === 'running'
            ? 'Scanning ledger...'
            : ghostCleanupState === 'done'
            ? 'Done!'
            : 'Clean Ghost Ads'}
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="filters-bar-products">
        <div className="search-box-products">
          <svg className="search-icon-products" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
          </svg>
          <input type="text" placeholder="Search products..." className="search-input-products"
            value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          <kbd className="search-kbd">⌘K</kbd>
        </div>
        <select className="filter-dropdown-products" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select className="filter-dropdown-products" value={categoryFilter}
          onChange={e => { setCategoryFilter(e.target.value); setCurrentPage(1); }}>
          <option value="all">All Categories</option>
          {categories.filter(c => c !== 'all').map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="btn-export">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2"/>
            <polyline points="7 10 12 15 17 10" stroke="currentColor" strokeWidth="2"/>
            <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Export CSV
        </button>
        <button className="btn-filter">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </button>
        <button className="btn-add-product">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
            <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Add Product
        </button>

        {/* Rows per page */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Rows per page:
          </label>
          <input
            type="number"
            min="1"
            max="200"
            value={pageSizeInput}
            onChange={e => setPageSizeInput(e.target.value)}
            onBlur={handlePageSizeChange}
            onKeyDown={e => { if (e.key === 'Enter') handlePageSizeChange(); }}
            style={{
              width: 60, height: 36, padding: '0 8px',
              border: '1.5px solid #6366f1', borderRadius: 7,
              fontSize: 13, fontWeight: 700, textAlign: 'center', outline: 'none',
            }}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table-wrapper-products">
        <table className="products-table">
          <thead>
            <tr>
              <th className="th-checkbox">
                <input type="checkbox"
                  checked={selectedProducts.length === paginated.length && paginated.length > 0}
                  onChange={handleSelectAll} />
              </th>
              <th>PRODUCT</th>
              <th>AD ID / CODE</th>
              <th>PRICE</th>
              <th>STATUS</th>
              <th>CATEGORY</th>
              <th>CONTACT</th>
              <th>MEDIA</th>
              <th>PAYMENT</th>
              <th>VIEWS</th>
              <th>LIKES</th>
              <th>PREMIUM TYPE</th>
              <th>AD VALIDITY</th>
              <th style={{ minWidth: 140 }}>ADJUST VALIDITY</th>
              <th>DATE ADDED</th>
              <th className="th-actions">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan="16" className="empty-cell">
                  <div className="empty-state-products">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                      <path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <h3>No products found</h3>
                    <p>Try adjusting your search or filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map(product => {
                const statusStr  = getStatus(product);
                const category   = getCategory(product);
                const email      = product.posted_by_email || product.user || 'Anonymous';
                const contact    = product.contact || null;
                const baseDays   = getDaysRemaining(product);
                const adjDays    = validityCache[product.id] !== undefined
                                     ? validityCache[product.id]
                                     : baseDays;
                const isExpired  = adjDays === 0;
                const photoCount = Number(product.image_count  ?? 0);
                const videoCount = Number(product.video_count  ?? 0);
                const likes      = Number(product.like_count   ?? 0);
                const payAmount  = Number(product.payment_amount ?? 0);
                const payStatus  = product.payment_status ?? 'Unpaid';

                return (
                  <tr
                    key={product.id}
                    className={selectedProducts.includes(product.id) ? 'selected' : ''}
                    style={isExpired ? { background: '#fff8f8' } : undefined}
                  >
                    <td>
                      <input type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => toggleSelect(product.id)} />
                    </td>

                    {/* Product */}
                    <td>
                      <div className="product-cell">
                        <ProductThumb product={product} />
                        <div className="product-info">
                          <div className="product-name" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            {product.title}
                            {isExpired && (
                              <span style={{
                                fontSize: 9, fontWeight: 700, color: '#dc2626',
                                background: '#fef2f2', border: '1px solid #fca5a5',
                                padding: '1px 5px', borderRadius: 4,
                              }}>EXPIRED</span>
                            )}
                          </div>
                          <div className="product-user">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            {email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* AD ID / CODE — styled like super admin: AD-00069 + id #69 */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{
                          fontSize: 12, fontWeight: 700, color: '#6366f1',
                          fontFamily: 'monospace', whiteSpace: 'nowrap',
                        }}>
                          AD-{String(product.id).padStart(5, '0')}
                        </span>
                        <span style={{
                          fontSize: 10, color: '#94a3b8',
                          fontFamily: 'monospace', whiteSpace: 'nowrap',
                        }}>
                          id #{product.id}
                        </span>
                      </div>
                    </td>

                    <td className="price-cell">KES {parseFloat(product.price || 0).toLocaleString()}</td>
                    <td><span className={`status-badge ${statusStr.toLowerCase()}`}>{statusStr}</span></td>
                    <td className="category-cell">{category}</td>

                    {/* Contact */}
                    <td>
                      {contact ? (
                        <div className="contact-item">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          <span className="contact-text">{contact}</span>
                        </div>
                      ) : <span className="contact-na">—</span>}
                    </td>

                    {/* Media */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                                       color: photoCount > 0 ? '#0369a1' : '#94a3b8' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                            <polyline points="21 15 16 10 5 21" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          {photoCount}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                                       color: videoCount > 0 ? '#7c3aed' : '#94a3b8' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <polygon points="23 7 16 12 23 17 23 7" stroke="currentColor" strokeWidth="2"/>
                            <rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          {videoCount}
                        </span>
                      </div>
                    </td>

                    {/* Payment */}
                    <td className="payment-cell">
                      {payAmount > 0 ? (
                        <>
                          <div className="payment-amount">KES {payAmount.toLocaleString()}</div>
                          <span className={`pay-status-badge ${
                            payStatus?.toLowerCase() === 'paid' ? 'pay-paid'
                            : payStatus?.toLowerCase() === 'pending' ? 'pay-pending'
                            : payStatus?.toLowerCase() === 'failed' ? 'pay-failed'
                            : 'pay-paid'
                          }`}>
                            {payStatus?.toLowerCase() === 'paid'    ? '✅ Paid'
                           : payStatus?.toLowerCase() === 'pending' ? '⏳ Pending'
                           : payStatus?.toLowerCase() === 'failed'  ? '❌ Failed'
                           : '✅ Paid'}
                          </span>
                        </>
                      ) : (
                        <span className="pay-status-badge pay-free">
                          {(() => {
                            const plan = (product.premium_plan || '').toLowerCase();
                            if (plan === 'free' || !plan) return '🆓 Free';
                            const ps = (payStatus || '').toLowerCase();
                            if (ps === 'bypass' || ps === 'admin') return '🔓 Bypass';
                            return '🔓 No Payment';
                          })()}
                        </span>
                      )}
                    </td>

                    <td className="views-cell">{product.views ?? 0}</td>

                    {/* Likes */}
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                     fontSize: 13, fontWeight: 600,
                                     color: likes > 0 ? '#e11d48' : '#94a3b8' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24"
                          fill={likes > 0 ? '#e11d48' : 'none'}
                          stroke={likes > 0 ? '#e11d48' : '#94a3b8'} strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                        {likes}
                      </span>
                    </td>

                    {/* Premium */}
                    <td><PremiumBadge product={product} /></td>

                    {/* AD VALIDITY */}
                    <td><AdValidityBadge days={baseDays} /></td>

                    {/* ADJUST VALIDITY */}
                    <td>
                      <AdjustValidityCell
                        product={product}
                        currentDays={adjDays}
                        onAdjusted={handleValidityAdjusted}
                      />
                    </td>

                    {/* Date */}
                    <td className="date-cell">{formatDate(product.date_added || product.created_at)}</td>

                    {/* Actions */}
                    <td>
                      <div className="actions-cell">
                        <button className="action-btn-products edit" title="Edit">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                        {isExpired && (
                          <button
                            title="Restore: add 14 days"
                            onClick={() => handleValidityAdjusted(product.id, 14, product.title)}
                            style={{
                              padding: '4px 8px', borderRadius: 6,
                              border: '1.5px solid #86efac', background: '#f0fdf4',
                              color: '#16a34a', fontSize: 11, fontWeight: 700,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >↺ Restore</button>
                        )}
                        <button
                          className="action-btn-products delete"
                          title={isExpired ? 'Delete expired ad' : 'Delete'}
                          onClick={() => setDeleteModal(product)}
                          style={isExpired ? { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626' } : undefined}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="pagination-products">
          <div className="pagination-info">
            {firstIdx + 1}–{Math.min(lastIdx, filtered.length)} of {filtered.length}
          </div>
          <div className="pagination-controls">
            <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.max(p-1,1))} disabled={currentPage===1}>‹</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let n;
              if (totalPages <= 5) n = i + 1;
              else if (currentPage <= 3) n = i + 1;
              else if (currentPage >= totalPages - 2) n = totalPages - 4 + i;
              else n = currentPage - 2 + i;
              return (
                <button key={n} className={`pagination-btn ${currentPage===n?'active':''}`}
                  onClick={() => setCurrentPage(n)}>{n}</button>
              );
            })}
            <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.min(p+1,totalPages))} disabled={currentPage===totalPages}>›</button>
          </div>
          <div className="pagination-info">Page {currentPage} of {totalPages}</div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      <DeleteConfirmModal
        product={deleteModal}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal(null)}
      />
    </div>
  );
}

export default ProductsView;

